import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialStateLabel, getPriceTypeLabel, materialStateEnum, priceTypeEnum, stepModeEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailyMachineMutation, useDailyMachineMutation } from '../../../../hooks/mutations/useDailyMachineMutation';
import { useMachinesQuery } from '../../../../hooks/queries/useMachinesQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DataItem } from '../../../../services/apiSLTP/dailyReports/activities';
import { DailyMachine } from '../../../../services/apiSLTP/dailyReports/dailyMachines';
import { Machine } from '../../../../services/apiSLTP/materials/machines';
import { removeDuplicates } from '../../../../utils/array';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageDailyMachineModalProps extends ModalProps {
    activityId: string;
    canChooseActivity?: boolean;
    refetch: () => void;
    dailyMachine: DailyMachine | null;
    machine: Machine | null;
    activities: DataItem[];
    site?: { name: string; id: string };
    siteName?: string;
    createTransfer: boolean;
    stepMode?: number;
}

const ManageDailyMachineModal: React.FC<ManageDailyMachineModalProps> = ({
    open,
    handleClose,
    refetch,
    activityId,
    dailyMachine,
    canChooseActivity = false,
    activities,
    machine,
    site,
    siteName,
    createTransfer,

    stepMode,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailyMachineId, setCurrentDailyMachineId] = useState(dailyMachine?._id || '');
    const [selectedMachine, setSelectedMachine] = useState({ label: '', value: '' });
    const [selectedSite, setSelectedSite] = useState({ label: '', value: '' });
    const [selectedActivity, setSelectedActivity] = useState({ label: '', value: '' });
    const [isCreateTransfer, setIsCreateTransfer] = useState(createTransfer);
    const [selectedState, setSelectedState] = useState<{
        label: string;
        value: string;
    }>({ label: capitalizeFirstLetter(t('present')), value: materialStateEnum.PRESENT.toString() });

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.NEW))), value: materialStateEnum.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.PRESENT))), value: materialStateEnum.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.REMOVE))), value: materialStateEnum.REMOVE.toString() },
    ];
    const [price, setPrice] = useState(typeof dailyMachine?.price === 'number' ? dailyMachine?.price.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        dailyMachine?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeLabel(dailyMachine?.priceType))),
                  value: (dailyMachine?.priceType).toString(),
              }
            : null
    );

    const { data: dataMachines } = useMachinesQuery({ queries: { active: true } });

    const { mutate: updateDailyMachine } = useDailyMachineMutation({ onSuccess: refetch });
    const { mutate: createDailyMachine } = useCreateDailyMachineMutation({ onSuccess: refetch });

    const postDailyMachine = () => {
        createDailyMachine({
            activityId: activityId || selectedActivity.value,
            machineId: selectedMachine.value,
            state: parseInt(selectedState.value),
            createTransfer: isCreateTransfer,
            isUsed: true,
            quantity: 1,
        });
    };

    const patchDailyMachine = () => {
        updateDailyMachine({
            id: currentDailyMachineId,
            body: {
                machineId: selectedMachine.value,
                state: parseInt(selectedState.value),
                createTransfer: isCreateTransfer,
                price: price ? parseFloat(price) : null,
                priceType: priceType ? parseInt(priceType?.value) : null,
            },
            queries: {
                syncTransfers: true,
            },
        });
    };

    useEffect(() => {
        if (dailyMachine) {
            setSelectedMachine({ label: dailyMachine.machineId?.designation || '', value: dailyMachine.machineId?._id || '' });
            setSelectedState({
                label: capitalizeFirstLetter(t(getMaterialStateLabel(dailyMachine.state))) || '',
                value: dailyMachine.state.toString() || '',
            });
        } else if (machine) {
            setSelectedMachine({ label: machine?.designation || '', value: machine?._id || '' });
        }
        if (canChooseActivity && site) {
            setSelectedSite({ label: site?.name, value: site?.id });
        }
    }, []);

    return (
        <ModalBody
            title={t('machine')}
            subtitle={!!currentDailyMachineId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentDailyMachineId.length ? postDailyMachine() : patchDailyMachine();
            }}
            disableSubmit={!selectedMachine.value.length || (canChooseActivity && !selectedActivity.value.length)}
        >
            {canChooseActivity && (
                <>
                    <DropDown
                        selectedItem={selectedSite}
                        items={
                            removeDuplicates(
                                activities.map((activity) => activity.siteId),
                                ['_id']
                            ).map((site) => ({
                                label: site.name,
                                value: site._id,
                            })) || []
                        }
                        handleChange={(event, value) => {
                            setSelectedActivity({ label: '', value: '' });
                            setSelectedSite({ value: value?.value || '', label: value?.label || '' });
                        }}
                        inputLabel={t('site')}
                        required
                    />
                    {!!selectedSite.value.length && (
                        <DropDown
                            selectedItem={selectedActivity}
                            items={
                                activities
                                    .filter((activity) => activity.siteId._id === selectedSite.value)
                                    .map((activity) => ({
                                        label: activity.reportId.userId.firstName + ' ' + activity.reportId.userId.lastName,
                                        value: activity._id,
                                    })) || []
                            }
                            handleChange={(event, value) => setSelectedActivity({ value: value?.value || '', label: value?.label || '' })}
                            inputLabel={t('person')}
                            required
                        />
                    )}
                </>
            )}
            <DropDown
                selectedItem={selectedMachine}
                items={
                    dataMachines?.map((machine) => ({
                        label: machine.designation,
                        value: machine._id,
                        group: machine.machineTypeId.designation,
                    })) || []
                }
                handleChange={(event, value) => setSelectedMachine({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('machine')}
                grouped
                required
            />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => setSelectedState({ value: value?.value || '', label: value?.label || '' })}
                inputLabel={t('state')}
                required
            />
            {stepMode !== stepModeEnum.VALIDATE && (
                <ToggleLine
                    checked={isCreateTransfer}
                    onChange={(checked) => setIsCreateTransfer(checked)}
                    title={capitalizeFirstLetter(t('createTransfer'))}
                    disabled={parseInt(selectedState.value) === materialStateEnum.PRESENT}
                />
            )}
            {stepMode === stepModeEnum.VALIDATE && (
                <>
                    <CustomTextInput
                        value={price}
                        onChange={setPrice}
                        label={t('price')}
                        errorText={t('error')}
                        startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                        type="number"
                        placeholder={`${t('default')} - ${dailyMachine?.machineId.price ?? dailyMachine?.machineId.machineTypeId.price}`}
                    />
                    <DropDown
                        items={Object.values(priceTypeEnum).map((type) => ({
                            label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                            value: type.toString(),
                        }))}
                        selectedItem={priceType}
                        handleChange={(e, value) => {
                            setPriceType(value);
                        }}
                        inputLabel={t('pricing')}
                        disableClearable={false}
                        placeholder={`${t('default')} - ${capitalizeFirstLetter(t(getPriceTypeLabel(dailyMachine?.machineId.priceType ?? dailyMachine?.machineId.machineTypeId.priceType)))}`}
                    />
                </>
            )}
        </ModalBody>
    );
};

export default ManageDailyMachineModal;
